import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, ArrowRight, Check, MessageCircle, Loader2 } from 'lucide-react'
import { chatService } from '@/lib/api/services/chat.service'
import { freelancerService } from '@/lib/api/services/freelancer.service'
import { useProfile } from '@/lib/hooks/use-profile'
import { toast } from 'sonner'

interface FreelancerCardProps {
  freelancer: {
    id?: string | null
    user_id?: string | null // Clerk user ID (optional, will be fetched if not provided)
    display_name?: string | null
    profile_picture_url?: string | null
    headline?: string | null
    location?: string | null
    hourly_rate_cents?: number | null
    rate_range_min_cents?: number | null
    rate_range_max_cents?: number | null
    skills?: unknown // Allow Json type from database
    [key: string]: unknown // Allow additional properties
  }
  showSecondCheckIcon?: boolean
  clickable?: boolean
  onCardClick?: () => void
}

const getInitials = (name: string | null) => {
  if (!name) return '?'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

const formatRateRange = (minCents: number | null, maxCents: number | null) => {
  const min = minCents ? Math.round(minCents / 100) : null
  const max = maxCents ? Math.round(maxCents / 100) : null

  if (min && max) {
    if (min === max) {
      return `$${min}/hr`
    }
    return `$${min} - $${max}/hr`
  }
  if (min) {
    return `$${min}+/hr`
  }
  if (max) {
    return `up to $${max}/hr`
  }
  return null
}

export function FreelancerCard({
  freelancer,
  showSecondCheckIcon = false,
  clickable = false,
  onCardClick,
}: FreelancerCardProps) {
  const navigate = useNavigate()
  const { profile } = useProfile()
  const [isStartingChat, setIsStartingChat] = useState(false)
  const allSkills = Array.isArray(freelancer.skills) ? (freelancer.skills as string[]) : []
  const maxVisibleSkills = 3
  const visibleSkills = allSkills.slice(0, maxVisibleSkills)
  const remainingSkillsCount = Math.max(0, allSkills.length - maxVisibleSkills)
  const initials = getInitials(freelancer.display_name ?? null)
  const rate = formatRateRange(
    freelancer.rate_range_min_cents ?? null,
    freelancer.rate_range_max_cents ?? null
  )

  // Check if user is a client (only clients can start chats from freelancer cards)
  const isClient = profile?.is_client ?? false

  const handleCardClick = () => {
    if (!freelancer.id) return
    if (clickable && onCardClick) {
      onCardClick()
    } else if (clickable) {
      navigate(`/genius/${freelancer.id}`)
    }
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!freelancer.id) return
    navigate(`/genius/${freelancer.id}`)
  }

  const handleStartChat = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!freelancer.id) return

    // Only clients can start chats
    if (!isClient) {
      toast.error('Only clients can start conversations with freelancers')
      return
    }

    setIsStartingChat(true)
    try {
      // Get the user_id from the freelancer profile
      // The freelancer.id is the profile ID, we need to fetch the profile to get user_id
      let freelancerUserId: string
      
      // Check if user_id is already available in the freelancer object
      if (freelancer.user_id && typeof freelancer.user_id === 'string') {
        freelancerUserId = freelancer.user_id
      } else {
        // Fetch the profile to get the user_id
        const profile = await freelancerService.getProfile(freelancer.id)
        if (!profile.user_id) {
          throw new Error('Freelancer user ID not found')
        }
        freelancerUserId = profile.user_id
      }

      // Create or find existing conversation using user_id (not profile ID)
      const conversation = await chatService.createOrFindConversation(freelancerUserId)
      
      // Navigate to chat page with conversation ID
      navigate(`/client/chat?conversationId=${conversation.id}`)
    } catch (error: any) {
      console.error('Failed to start chat:', error)
      toast.error(error.message || 'Failed to start chat. Please try again.')
    } finally {
      setIsStartingChat(false)
    }
  }

  return (
    <div
      className={`rounded-2xl sm:rounded-[32px] border border-slate-200 p-5 sm:p-7 lg:p-[43px] overflow-hidden h-full flex flex-col ${
        clickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
      }`}
      style={{
        background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
      }}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex-shrink-0 mb-3 sm:mb-4">
        <div className="flex gap-3 sm:gap-4 min-w-0">
          <div className="relative flex-shrink-0">
            <div
              className="w-12 h-12 sm:w-[65px] sm:h-[65px] rounded-2xl sm:rounded-[32px] flex items-center justify-center overflow-hidden"
              style={{
                background: freelancer.profile_picture_url
                  ? 'transparent'
                  : 'linear-gradient(135deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                boxShadow: '0px 4px 12px 0px rgba(70, 105, 237, 0.2)',
              }}
            >
              {freelancer.profile_picture_url ? (
                <img
                  src={freelancer.profile_picture_url}
                  alt={freelancer.display_name || 'Genius'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-black text-lg sm:text-2xl">{initials}</span>
              )}
            </div>
            {/* Online indicator */}
            <div
              className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-full bg-emerald-500 border-2 sm:border-[3px] border-white z-10"
              style={{ boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.2)' }}
            />
          </div>

          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 mb-1 min-w-0">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate flex-1 min-w-0">
                {freelancer.display_name}
              </h3>
              {showSecondCheckIcon && (
                <div className="w-5 h-5 sm:w-[22px] sm:h-[22px] rounded-md bg-[#4669ed] flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-[15px] text-slate-700 min-w-0">
              {freelancer.location && (
                <span className="flex items-center gap-1 min-w-0 flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{freelancer.location}</span>
                </span>
              )}
              {rate && <span className="flex-shrink-0">💰 {rate}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mb-3 sm:mb-4">
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-3 sm:mb-4 line-clamp-2 overflow-hidden">
          {freelancer.headline || 'AI Expert'}
        </p>

        <div className="flex flex-wrap gap-1.5 sm:gap-2 overflow-hidden">
          {visibleSkills.map((skill, idx) => (
            <span
              key={idx}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full sm:rounded-[20px] bg-[#d1e0fc] text-[#2b42a3] font-semibold text-xs sm:text-[13px] flex-shrink-0"
            >
              {skill}
            </span>
          ))}
          {remainingSkillsCount > 0 && (
            <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full sm:rounded-[20px] bg-slate-200 text-slate-600 font-semibold text-xs sm:text-[13px] flex-shrink-0">
              +{remainingSkillsCount} more
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 border-t-2 border-slate-200 pt-4 sm:pt-6 flex-shrink-0 mt-auto">
        {isClient && (
          <button
            onClick={handleStartChat}
            disabled={isStartingChat}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-[22px] py-2 sm:py-3 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all bg-white border-slate-200 text-slate-700 hover:border-slate-300`}
          >
            {isStartingChat ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 text-slate-400" />
                Starting...
              </>
            ) : (
              <>
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                Start Chat
              </>
            )}
          </button>
        )}
        <button
          onClick={handleButtonClick}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-[22px] py-2 sm:py-3 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all bg-[#4669ed] border-[#4669ed] text-white hover:bg-[#4669ed]/90 hover:border-[#4669ed]/90`}
        >
          View Profile
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </button>
      </div>
    </div>
  )
}

